
import {
  computed, defineComponent, ref, watch,
} from 'vue';
import type { PropType, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
// eslint-disable-next-line import/no-cycle
import { TemplateEditorState } from '@/composables/template-editor/TemplateEditor';
// eslint-disable-next-line import/no-cycle
import { useStore } from '@/store';
import { getShopDefaultLang, UserState } from '@/composables/User';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { getTemplateIframeDocument } from '@/components/template-builder/utils/helpers';

export default defineComponent({
  name: 'SendTestV4Preview',

  emits: ['update:lang'],

  props: {
    contactId: {
      type: null as unknown as PropType<number | null>,
      default: null,
    },
    lang: {
      type: String as PropType<string>,
      default: '',
    },
    availableLangs: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    conditionOverrides: {
      type: Object as PropType<Record<string, boolean>>,
      default: () => ({}),
    },
    channel: {
      type: String as PropType<'email' | 'sms' | 'whatsapp' | 'richtexting'>,
      default: 'email',
    },
  },

  setup(props, { emit }) {
    const { t } = useI18n();
    const store = useStore();

    const previewHtml: Ref<string | null> = ref(null);
    const previewHtmlSize: Ref<number | null> = ref(null);
    const previewLoading = ref(false);
    const previewError: Ref<string | null> = ref(null);
    const previewIframeRef = ref();
    const viewMode = ref<'desktop' | 'mobile'>('desktop');
    const resolvePreviewChannel = computed((): 'email' | 'sms' | 'whatsapp' | 'richtexting' => {
      if (
        props.channel === 'sms'
        || props.channel === 'whatsapp'
        || props.channel === 'richtexting'
        || props.channel === 'email'
      ) {
        return props.channel;
      }
      const templateType = TemplateEditorState.template?.type;
      if (templateType === 'sms' || templateType === 'whatsapp' || templateType === 'richtexting') {
        return templateType;
      }
      return 'email';
    });

    const previewTitleKey = computed(() => {
      if (resolvePreviewChannel.value === 'sms') {
        return 'templateBuilder.modals.sendTestV4.previewTitleSms';
      }
      if (resolvePreviewChannel.value === 'whatsapp') {
        return 'templateBuilder.modals.sendTestV4.previewTitleWhatsapp';
      }
      if (resolvePreviewChannel.value === 'richtexting') {
        return 'templateBuilder.modals.sendTestV4.previewTitleRcs';
      }
      return 'templateBuilder.modals.sendTestV4.previewTitle';
    });

    const escapeHtml = (value: string) => value
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');

    const buildSmsFallbackHtml = (textContent: string) => {
      const safeText = escapeHtml(textContent || '');
      const shopName = escapeHtml(UserState.activeShop?.name ?? 'Shop');
      return `<!doctype html>
<html>
<head>
  <meta charset="utf-8">
</head>
<body>
  <spm-template>
    <div id="spm_body">
      <div class="sms-status-bar spm_no_translate">
        <span class="sms-status-bar__time">09:41</span>
        <span class="sms-status-bar__notch"></span>
        <span class="sms-status-bar__icons">
          <span class="sms-status-bar__icon-signal"></span>
          <span class="sms-status-bar__icon-wifi"></span>
          <span class="sms-status-bar__icon-battery"></span>
        </span>
      </div>
      <div class="sms-preview-header spm_no_translate">
        <span class="sms-preview-header__avatar"></span>
        <span class="sms-preview-header__name">${shopName}</span>
      </div>
      <div class="sms-messages-area">
        <div class="sms-bubble">
          ${safeText}
        </div>
      </div>
    </div>
  </spm-template>
</body>
</html>`;
    };

    const buildWhatsappFallbackHtml = (content: string, asHtml = false) => {
      const safeText = escapeHtml(content || '');
      const shopName = escapeHtml(UserState.activeShop?.name ?? 'Shop');
      const messageContent = asHtml ? content : `
        <div class="whatsapp-message">
          <div class="whatsapp-message__body">
            <div class="whatsapp-message__text">${safeText}</div>
          </div>
          <div class="whatsapp-message__footer">
            <span class="whatsapp-message__hour">09:41</span>
          </div>
        </div>
      `;
      return `<!doctype html>
<html>
<head>
  <meta charset="utf-8">
</head>
<body>
  <spm-template>
    <div id="spm_body" data-shop-name="${shopName}">
      <div class="whatsapp-status-bar spm_no_translate" data-do-not-select="1">
        <span class="whatsapp-status-bar__time">09:41</span>
        <span class="whatsapp-status-bar__notch"></span>
        <span class="whatsapp-status-bar__icons">
          <span class="whatsapp-status-bar__icon-signal"></span>
          <span class="whatsapp-status-bar__icon-wifi"></span>
          <span class="whatsapp-status-bar__icon-battery"></span>
        </span>
      </div>
      <div class="whatsapp-preview-header spm_no_translate" data-do-not-select="1">
        <span class="whatsapp-preview-header__back"></span>
        <span class="whatsapp-preview-header__avatar"></span>
        <span class="whatsapp-preview-header__name">${shopName}</span>
        <span class="whatsapp-preview-header__icons"></span>
      </div>
      <div class="whatsapp-messages-area" data-do-not-select="1">
        ${messageContent}
      </div>
    </div>
  </spm-template>
</body>
</html>`;
    };

    const enrichSmsPreviewHtml = (html: string): string => {
      const cssHref = '/css/template-builder/baseIframeSms.css?v=20260213e';
      const smsExtraStyles = '<style>html, body { margin: 0 !important; background: #ffffff !important; } spm-template { background: #ffffff !important; }</style>';
      let resolvedHtml = html;

      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const spmBody = doc.querySelector('#spm_body');
        if (spmBody && !spmBody.querySelector('.sms-messages-area')) {
          const statusBar = doc.createElement('div');
          statusBar.className = 'sms-status-bar spm_no_translate';
          statusBar.innerHTML = [
            '<span class="sms-status-bar__time">09:41</span>',
            '<span class="sms-status-bar__notch"></span>',
            '<span class="sms-status-bar__icons">',
            '<span class="sms-status-bar__icon-signal"></span>',
            '<span class="sms-status-bar__icon-wifi"></span>',
            '<span class="sms-status-bar__icon-battery"></span>',
            '</span>',
          ].join('');

          const smsHeader = doc.createElement('div');
          smsHeader.className = 'sms-preview-header spm_no_translate';
          smsHeader.innerHTML = `<span class="sms-preview-header__avatar"></span><span class="sms-preview-header__name">${UserState.activeShop?.name ?? 'Shop'}</span>`;

          const messagesArea = doc.createElement('div');
          messagesArea.className = 'sms-messages-area';
          const fragment = doc.createDocumentFragment();
          while (spmBody.firstChild) {
            fragment.appendChild(spmBody.firstChild);
          }
          if (!(fragment as unknown as ParentNode).querySelector?.('.sms-bubble')) {
            const bubble = doc.createElement('div');
            bubble.className = 'sms-bubble';
            bubble.appendChild(fragment);
            messagesArea.appendChild(bubble);
          } else {
            messagesArea.appendChild(fragment);
          }
          spmBody.appendChild(statusBar);
          spmBody.appendChild(smsHeader);
          spmBody.appendChild(messagesArea);
        } else if (!spmBody) {
          const textFallback = (doc.body?.textContent ?? html ?? '').trim();
          resolvedHtml = buildSmsFallbackHtml(textFallback);
          const cssLink = `<link rel="stylesheet" href="${cssHref}" data-do-not-rewrite-url="1">`;
          return resolvedHtml.replace('</head>', `${cssLink}${smsExtraStyles}</head>`);
        }
        const existingArea = doc.querySelector('.sms-messages-area');
        if (existingArea && !existingArea.querySelector('.sms-bubble')) {
          const bubble = doc.createElement('div');
          bubble.className = 'sms-bubble';
          while (existingArea.firstChild) {
            bubble.appendChild(existingArea.firstChild);
          }
          existingArea.appendChild(bubble);
        }

        resolvedHtml = doc.documentElement.outerHTML;
      } catch {
        resolvedHtml = buildSmsFallbackHtml(String(html || '').trim());
      }

      const cssLink = `<link rel="stylesheet" href="${cssHref}" data-do-not-rewrite-url="1">`;
      if (resolvedHtml.includes('</head>')) {
        return resolvedHtml.replace('</head>', `${cssLink}${smsExtraStyles}</head>`);
      }
      return `<!doctype html><html><head><meta charset="utf-8">${cssLink}${smsExtraStyles}</head><body>${resolvedHtml}</body></html>`;
    };

    const enrichWhatsappPreviewHtml = (html: string): string => {
      const cssHref = '/css/template-builder/baseIframeWhatsapp.css?v=20260213f';
      let resolvedHtml = html;
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const spmBody = doc.querySelector('#spm_body');
        const shopName = UserState.activeShop?.name ?? 'Shop';
        if (spmBody) {
          spmBody.setAttribute('data-shop-name', shopName);
          const allStatusBars = Array.from(spmBody.querySelectorAll('.whatsapp-status-bar'));
          const allHeaders = Array.from(spmBody.querySelectorAll('.whatsapp-preview-header'));

          let statusBar = allStatusBars[0] ?? null;
          let header = allHeaders[0] ?? null;

          if (!statusBar) {
            statusBar = doc.createElement('div');
            statusBar.className = 'whatsapp-status-bar spm_no_translate';
            statusBar.setAttribute('data-do-not-select', '1');
            statusBar.innerHTML = [
              '<span class="whatsapp-status-bar__time">09:41</span>',
              '<span class="whatsapp-status-bar__notch"></span>',
              '<span class="whatsapp-status-bar__icons">',
              '<span class="whatsapp-status-bar__icon-signal"></span>',
              '<span class="whatsapp-status-bar__icon-wifi"></span>',
              '<span class="whatsapp-status-bar__icon-battery"></span>',
              '</span>',
            ].join('');
          }

          if (!header) {
            header = doc.createElement('div');
            header.className = 'whatsapp-preview-header spm_no_translate';
            header.setAttribute('data-do-not-select', '1');
            header.innerHTML = [
              '<span class="whatsapp-preview-header__back"></span>',
              '<span class="whatsapp-preview-header__avatar"></span>',
              `<span class="whatsapp-preview-header__name">${shopName}</span>`,
              '<span class="whatsapp-preview-header__icons"></span>',
            ].join('');
          }

          // Remove all duplicates except the primary instances.
          allStatusBars.slice(1).forEach((node) => node.remove());
          allHeaders.slice(1).forEach((node) => node.remove());

          statusBar.classList.add('spm_no_translate');
          header.classList.add('spm_no_translate');
          statusBar.setAttribute('data-do-not-select', '1');
          header.setAttribute('data-do-not-select', '1');
          const nameEl = header.querySelector('.whatsapp-preview-header__name');
          if (nameEl) nameEl.textContent = shopName;

          // Move shell to top-level under #spm_body.
          if (statusBar.parentNode) statusBar.parentNode.removeChild(statusBar);
          if (header.parentNode) header.parentNode.removeChild(header);
          spmBody.insertBefore(statusBar, spmBody.firstChild);
          spmBody.insertBefore(header, statusBar.nextSibling);

          // Ensure a single top-level messages area.
          let messagesArea = spmBody.querySelector(':scope > .whatsapp-messages-area') as HTMLElement | null;
          if (!messagesArea) {
            messagesArea = doc.createElement('div');
            messagesArea.className = 'whatsapp-messages-area';
            messagesArea.setAttribute('data-do-not-select', '1');
            spmBody.appendChild(messagesArea);
          } else {
            messagesArea.setAttribute('data-do-not-select', '1');
          }

          // Move all non-shell top-level nodes into messages area.
          const topLevelNodes = Array.from(spmBody.childNodes);
          topLevelNodes.forEach((node) => {
            if (node === statusBar || node === header || node === messagesArea) return;
            messagesArea!.appendChild(node);
          });

          // Remove any shell duplicates that may still exist inside messages content.
          messagesArea
            .querySelectorAll('.whatsapp-status-bar, .whatsapp-preview-header')
            .forEach((node) => node.remove());
        } else {
          const bodyInnerHtml = (doc.body?.innerHTML ?? '').trim();
          const bodyText = (doc.body?.textContent ?? html ?? '').trim();
          if (bodyInnerHtml && bodyInnerHtml !== bodyText) {
            resolvedHtml = buildWhatsappFallbackHtml(bodyInnerHtml, true);
          } else {
            resolvedHtml = buildWhatsappFallbackHtml(bodyText, false);
          }
        }
        if (spmBody) {
          resolvedHtml = doc.documentElement.outerHTML;
        }
      } catch {
        const raw = String(html || '').trim();
        if (raw.includes('<') && raw.includes('>')) {
          resolvedHtml = buildWhatsappFallbackHtml(raw, true);
        } else {
          resolvedHtml = buildWhatsappFallbackHtml(raw, false);
        }
      }

      // Final defensive cleanup: keep only one WhatsApp status bar/header
      // even when upstream preview HTML is already duplicated.
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(resolvedHtml, 'text/html');
        const allStatusBars = Array.from(doc.querySelectorAll('.whatsapp-status-bar'));
        const allHeaders = Array.from(doc.querySelectorAll('.whatsapp-preview-header'));

        if (allStatusBars.length > 1) {
          allStatusBars.slice(1).forEach((node) => node.remove());
        }
        if (allHeaders.length > 1) {
          allHeaders.slice(1).forEach((node) => node.remove());
        }

        const spmBody = doc.querySelector('#spm_body');
        if (spmBody) {
          const statusBar = doc.querySelector('.whatsapp-status-bar');
          const header = doc.querySelector('.whatsapp-preview-header');
          if (statusBar && statusBar.parentNode !== spmBody) {
            statusBar.parentNode?.removeChild(statusBar);
            spmBody.insertBefore(statusBar, spmBody.firstChild);
          }
          if (header && header.parentNode !== spmBody) {
            header.parentNode?.removeChild(header);
            const first = spmBody.firstChild;
            if (first) {
              spmBody.insertBefore(header, first.nextSibling);
            } else {
              spmBody.appendChild(header);
            }
          }
        }

        resolvedHtml = doc.documentElement.outerHTML;
      } catch {
        // keep resolvedHtml as-is when defensive cleanup fails
      }

      const cssLink = `<link rel="stylesheet" href="${cssHref}" data-do-not-rewrite-url="1">`;
      if (resolvedHtml.includes('</head>')) {
        return resolvedHtml.replace('</head>', `${cssLink}</head>`);
      }
      return `<!doctype html><html><head><meta charset="utf-8">${cssLink}</head><body>${resolvedHtml}</body></html>`;
    };

    const parseRichtextingSmartlistEnabled = (value: unknown): boolean | null => {
      if (value === true || value === 'true' || value === 1 || value === '1') {
        return true;
      }
      if (value === false || value === 'false' || value === 0 || value === '0') {
        return false;
      }
      return null;
    };

    const resolveRcsSmartlistEnabledForPreview = (): boolean | null => {
      const iframeDoc = getTemplateIframeDocument();
      const liveCarousel = iframeDoc?.querySelector('.rcs-smartlist-carousel');
      if (liveCarousel?.classList.contains('smartlist-enabled')) {
        return true;
      }

      const previewLang = props.lang || getShopDefaultLang();
      const template = TemplateEditorState.template;
      const configuration = template?.configuration as Record<string, any> | undefined;
      const richtextingData = configuration?.richtexting_data as Record<string, any> | undefined;

      if (richtextingData) {
        const smartlist = richtextingData[previewLang]?.smartlist
          ?? richtextingData[getShopDefaultLang()]?.smartlist
          ?? richtextingData[Object.keys(richtextingData)[0]]?.smartlist;
        const fromConfig = parseRichtextingSmartlistEnabled(smartlist?.enabled);
        if (fromConfig !== null) {
          return fromConfig;
        }
      }

      return null;
    };

    const mergeEditorRcsCarouselIntoPreview = (doc: Document): void => {
      const smartlistEnabled = resolveRcsSmartlistEnabledForPreview();
      if (smartlistEnabled !== true) {
        return;
      }

      const iframeDoc = getTemplateIframeDocument();
      if (!iframeDoc) {
        return;
      }

      const liveCarousel = iframeDoc.querySelector('.rcs-smartlist-carousel');
      const liveCards = liveCarousel?.querySelector('.rcs-smartlist-cards');
      if (!liveCarousel || !liveCards || liveCards.children.length === 0) {
        return;
      }

      const previewRoot = findSpmBody(doc) || doc.body;
      const previewMessagesArea = (
        previewRoot.querySelector('.rcs-messages-area')
        || previewRoot
      ) as HTMLElement;

      let previewCarousel = previewMessagesArea.querySelector('.rcs-smartlist-carousel') as HTMLElement | null;
      if (!previewCarousel) {
        const rcsMessage = previewMessagesArea.querySelector('.rcs-message');
        if (!rcsMessage) {
          return;
        }
        previewCarousel = doc.createElement('div');
        previewCarousel.className = 'rcs-smartlist-carousel smartlist-enabled spm_no_translate';
        previewCarousel.setAttribute('data-do-not-select', '1');
        rcsMessage.after(previewCarousel);
      } else {
        previewCarousel.classList.add('smartlist-enabled');
      }

      let cardsContainer = previewCarousel.querySelector('.rcs-smartlist-cards') as HTMLElement | null;
      if (!cardsContainer) {
        cardsContainer = doc.createElement('div');
        cardsContainer.className = 'rcs-smartlist-cards spm_no_translate';
        previewCarousel.appendChild(cardsContainer);
      }

      cardsContainer.innerHTML = liveCards.innerHTML;

      const liveButtonSource = liveCarousel.querySelector('.rcs-smartlist-buttontext-source');
      if (liveButtonSource && !previewCarousel.querySelector('.rcs-smartlist-buttontext-source')) {
        previewCarousel.appendChild(liveButtonSource.cloneNode(true));
      }
    };

    const findSpmBody = (doc: Document): HTMLElement | null => (
      doc.querySelector('#spm_body')
      || doc.querySelector('[id="spm_body"]')
      || doc.querySelector('spm-template #spm_body')
      || doc.querySelector('spm-template')
    );

    const stripSmsPreviewArtifacts = (root: ParentNode): void => {
      root.querySelectorAll(
        '.sms-status-bar, .sms-preview-header, .sms-messages-area',
      ).forEach((node) => node.remove());

      root.querySelectorAll('.sms-bubble').forEach((bubble) => {
        const parent = bubble.parentNode;
        if (!parent) {
          bubble.remove();
          return;
        }
        while (bubble.firstChild) {
          parent.insertBefore(bubble.firstChild, bubble);
        }
        bubble.remove();
      });
    };

    const syncRcsSmartlistEnabledState = (doc: Document): void => {
      const configEnabled = resolveRcsSmartlistEnabledForPreview();

      doc.querySelectorAll('.rcs-smartlist-carousel').forEach((carousel) => {
        const domEnabled = carousel.classList.contains('smartlist-enabled');
        const isEnabled = configEnabled === true || (configEnabled === null && domEnabled);

        if (isEnabled) {
          carousel.classList.add('smartlist-enabled');
          return;
        }

        if (configEnabled === false) {
          carousel.remove();
        }
      });
    };

    const buildRcsFallbackHtml = (html: string): string => {
      const cssHref = '/css/template-builder/baseIframeRichTexting.css?v=20260309p';
      const rcsExtraStyles = '<style>html, body { margin: 0 !important; background: #e8e8e8 !important; } spm-template { background: #e8e8e8 !important; }</style>';
      let messageText = '';

      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        stripSmsPreviewArtifacts(doc);
        syncRcsSmartlistEnabledState(doc);
        messageText = doc.querySelector('.rcs-message__text')?.textContent?.trim()
          || doc.querySelector('.rcs-message__body .rcs-message__text')?.textContent?.trim()
          || '';
      } catch {
        messageText = '';
      }

      const safeText = escapeHtml(messageText || '');
      const shopName = escapeHtml(UserState.activeShop?.name ?? 'Shop');
      const minimalRcsHtml = `<!doctype html><html><head><meta charset="utf-8"></head><body>
<spm-template><div id="spm_body" data-shop-name="${shopName}">
<div class="rcs-status-bar spm_no_translate" data-do-not-select="1">
<span class="rcs-status-bar__time">09:41</span><span class="rcs-status-bar__notch"></span>
<span class="rcs-status-bar__icons"><span class="rcs-status-bar__icon-signal"></span><span class="rcs-status-bar__icon-wifi"></span><span class="rcs-status-bar__icon-battery"></span></span>
</div>
<div class="rcs-preview-header spm_no_translate" data-do-not-select="1">
<span class="rcs-preview-header__back"></span><span class="rcs-preview-header__avatar"></span>
<span class="rcs-preview-header__name">${shopName}</span><span class="rcs-preview-header__icons"></span>
</div>
<div class="rcs-messages-area" data-do-not-select="1">
<div class="rcs-message"><div class="rcs-message__body"><div class="rcs-message__text">${safeText}</div></div></div>
</div>
</div></spm-template></body></html>`;

      const cssLink = `<link rel="stylesheet" href="${cssHref}" data-do-not-rewrite-url="1">`;
      return minimalRcsHtml.replace('</head>', `${cssLink}${rcsExtraStyles}</head>`);
    };

    const enrichRcsPreviewHtml = (html: string): string => {
      const cssHref = '/css/template-builder/baseIframeRichTexting.css?v=20260309p';
      const rcsExtraStyles = [
        '<style>',
        'html, body { margin: 0 !important; background: #e8e8e8 !important; }',
        'spm-template { background: #e8e8e8 !important; }',
        '.rcs-smartlist-carousel.smartlist-enabled { display: flex !important; }',
        'body:has(.rcs-smartlist-carousel.smartlist-enabled) .rcs-message .rcs-message__body { display: none !important; }',
        'body:has(.rcs-smartlist-carousel.smartlist-enabled) .rcs-message .rcs-message__header,',
        'body:has(.rcs-smartlist-carousel.smartlist-enabled) .rcs-message .rcs-message__title,',
        'body:has(.rcs-smartlist-carousel.smartlist-enabled) .rcs-message .rcs-message__footer {',
        'position: absolute !important; left: -9999px !important; top: -9999px !important; pointer-events: none !important;',
        '}',
        '</style>',
      ].join('');
      let resolvedHtml = html;

      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        stripSmsPreviewArtifacts(doc);
        mergeEditorRcsCarouselIntoPreview(doc);
        syncRcsSmartlistEnabledState(doc);

        const spmBody = findSpmBody(doc);
        const shopName = UserState.activeShop?.name ?? 'Shop';

        const hasRcsPreviewContent = !!spmBody?.querySelector('.rcs-message, .rcs-smartlist-carousel');

        if (spmBody && hasRcsPreviewContent) {
          if (spmBody.id !== 'spm_body') {
            spmBody.setAttribute('id', 'spm_body');
          }
          spmBody.setAttribute('data-shop-name', shopName);

          const allStatusBars = Array.from(spmBody.querySelectorAll('.rcs-status-bar'));
          const allHeaders = Array.from(spmBody.querySelectorAll('.rcs-preview-header'));

          let statusBar = allStatusBars[0] ?? null;
          let header = allHeaders[0] ?? null;

          if (!statusBar) {
            statusBar = doc.createElement('div');
            statusBar.className = 'rcs-status-bar spm_no_translate';
            statusBar.setAttribute('data-do-not-select', '1');
            statusBar.innerHTML = [
              '<span class="rcs-status-bar__time">09:41</span>',
              '<span class="rcs-status-bar__notch"></span>',
              '<span class="rcs-status-bar__icons">',
              '<span class="rcs-status-bar__icon-signal"></span>',
              '<span class="rcs-status-bar__icon-wifi"></span>',
              '<span class="rcs-status-bar__icon-battery"></span>',
              '</span>',
            ].join('');
          }

          if (!header) {
            header = doc.createElement('div');
            header.className = 'rcs-preview-header spm_no_translate';
            header.setAttribute('data-do-not-select', '1');
            header.innerHTML = [
              '<span class="rcs-preview-header__back"></span>',
              '<span class="rcs-preview-header__avatar"></span>',
              `<span class="rcs-preview-header__name">${shopName}</span>`,
              '<span class="rcs-preview-header__icons"></span>',
            ].join('');
          }

          allStatusBars.slice(1).forEach((node) => node.remove());
          allHeaders.slice(1).forEach((node) => node.remove());

          const nameEl = header.querySelector('.rcs-preview-header__name');
          if (nameEl) nameEl.textContent = shopName;

          if (statusBar.parentNode) statusBar.parentNode.removeChild(statusBar);
          if (header.parentNode) header.parentNode.removeChild(header);
          spmBody.insertBefore(statusBar, spmBody.firstChild);
          spmBody.insertBefore(header, statusBar.nextSibling);

          let messagesArea = spmBody.querySelector(':scope > .rcs-messages-area') as HTMLElement | null;
          if (!messagesArea) {
            messagesArea = doc.createElement('div');
            messagesArea.className = 'rcs-messages-area';
            messagesArea.setAttribute('data-do-not-select', '1');
            spmBody.appendChild(messagesArea);
          }

          const topLevelNodes = Array.from(spmBody.childNodes);
          topLevelNodes.forEach((node) => {
            if (node === statusBar || node === header || node === messagesArea) return;
            messagesArea!.appendChild(node);
          });

          messagesArea
            .querySelectorAll('.rcs-status-bar, .rcs-preview-header, .sms-status-bar, .sms-preview-header, .sms-messages-area, .sms-bubble')
            .forEach((node) => node.remove());

          resolvedHtml = doc.documentElement.outerHTML;
        } else {
          const iframeDoc = getTemplateIframeDocument();
          const liveSpmBody = iframeDoc?.querySelector('#spm_body');
          if (liveSpmBody?.querySelector('.rcs-message')) {
            const editorShell = `<!doctype html><html><head><meta charset="utf-8"></head><body><spm-template>${liveSpmBody.outerHTML}</spm-template></body></html>`;
            return enrichRcsPreviewHtml(editorShell);
          }
          resolvedHtml = buildRcsFallbackHtml(html);
        }
      } catch {
        const iframeDoc = getTemplateIframeDocument();
        const liveSpmBody = iframeDoc?.querySelector('#spm_body');
        if (liveSpmBody?.querySelector('.rcs-message')) {
          const editorShell = `<!doctype html><html><head><meta charset="utf-8"></head><body><spm-template>${liveSpmBody.outerHTML}</spm-template></body></html>`;
          return enrichRcsPreviewHtml(editorShell);
        }
        resolvedHtml = buildRcsFallbackHtml(String(html || ''));
      }

      const cssLink = `<link rel="stylesheet" href="${cssHref}" data-do-not-rewrite-url="1">`;
      if (resolvedHtml.includes('</head>')) {
        return resolvedHtml.replace('</head>', `${cssLink}${rcsExtraStyles}</head>`);
      }
      return `<!doctype html><html><head><meta charset="utf-8">${cssLink}${rcsExtraStyles}</head><body>${resolvedHtml}</body></html>`;
    };

    const iframeSrcDoc = computed(() => {
      if (!previewHtml.value) return '';
      const channel = resolvePreviewChannel.value;
      if (channel === 'richtexting') {
        return enrichRcsPreviewHtml(previewHtml.value);
      }
      if (channel === 'sms') {
        return enrichSmsPreviewHtml(previewHtml.value);
      }
      if (channel === 'whatsapp') {
        return enrichWhatsappPreviewHtml(previewHtml.value);
      }
      return previewHtml.value;
    });

    const previewViewportClass = computed(() => {
      if (resolvePreviewChannel.value === 'richtexting') {
        return 'preview-viewport--rcs';
      }
      if (['sms', 'whatsapp'].includes(resolvePreviewChannel.value)) {
        return 'preview-viewport--sms';
      }
      return `preview-viewport--${viewMode.value}`;
    });

    const previewFrameWrapperClass = computed(() => {
      if (resolvePreviewChannel.value === 'richtexting') {
        return 'preview-frame-wrapper--rcs';
      }
      if (['sms', 'whatsapp'].includes(resolvePreviewChannel.value)) {
        return 'preview-frame-wrapper--sms';
      }
      return `preview-frame-wrapper--${viewMode.value}`;
    });

    const formatHtmlSize = (bytes: number | null): string => {
      if (bytes === null) return '';
      if (bytes < 1024) return `${bytes} B`;
      return `${(bytes / 1024).toFixed(1)} KB`;
    };

    const MAX_SIZE_KB = 100;

    const sizeBarPercent = computed(() => {
      if (!previewHtmlSize.value) return 0;
      const kb = previewHtmlSize.value / 1024;
      return Math.min((kb / MAX_SIZE_KB) * 100, 100);
    });

    const sizeBarColor = computed(() => {
      if (!previewHtmlSize.value) return '#94c748';
      const kb = previewHtmlSize.value / 1024;
      if (kb <= 90) return '#22c55e';
      if (kb <= 100) return '#f59e0b';
      return '#ef4444';
    });

    const handleSelectPreviewLang = (lang: string) => {
      emit('update:lang', lang);
    };

    const fetchPreview = async (
      contactId: number | null = null,
      lang: string | null = null,
      conditionOverrides: Record<string, boolean> = {},
    ) => {
      previewLoading.value = true;
      previewError.value = null;

      try {
        const idShop = UserState.activeShop?.id ?? 0;
        // Use TemplateEditorState when in template editor, fallback to store for flow context
        const idTemplate = TemplateEditorState.template?.id || store.state.general.sendTestEmailV4TemplateId || 0;

        const body: Record<string, any> = { idShop, idTemplate };
        if (contactId) body.contactId = contactId;
        if (lang) body.lang = lang;
        body.previewOptions = {
          annotateConditions: true,
        };
        if (Object.keys(conditionOverrides).length > 0) {
          body.conditionOverrides = conditionOverrides;
        }

        const result = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/template/preview-with-contact',
          {},
          '',
          body,
          true,
        );

        if (result && result.success) {
          previewHtml.value = result.html;
          previewHtmlSize.value = result.htmlSize;
        } else {
          previewError.value = result?.error || 'preview_failed';
        }
      } catch (error) {
        console.error('Error fetching preview:', error);
        previewError.value = 'preview_failed';
      } finally {
        previewLoading.value = false;
      }
    };

    // Fetch preview on mount (use current contactId if available)
    fetchPreview(props.contactId ?? null, props.lang || null, props.conditionOverrides || {});

    // Watch contactId and active preview language changes and re-fetch preview
    watch(
      () => [props.contactId, props.lang, JSON.stringify(props.conditionOverrides || {})],
      ([newId, newLang]) => {
        fetchPreview(
          newId as number | null,
          (newLang as string) || null,
          props.conditionOverrides || {},
        );
      },
    );

    return {
      t,
      previewHtml,
      previewHtmlSize,
      previewLoading,
      previewError,
      previewIframeRef,
      viewMode,
      previewTitleKey,
      iframeSrcDoc,
      previewViewportClass,
      previewFrameWrapperClass,
      handleSelectPreviewLang,
      formatHtmlSize,
      sizeBarPercent,
      sizeBarColor,
    };
  },
});
